import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, ClipboardList } from 'lucide-react';
import { useAuth, usePermissions } from '@contexts/AuthContext';
import { assessmentService } from '@services';
import { useAssessmentChat } from '@hooks/useAssessmentChat';
import PageLayout from '@components/layout/PageLayout';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import { StatusIcon } from '@components/common';
import { StatusCell } from '@components/common/table';
import DetailPageHeader from '@components/common/DetailPageHeader';
// Temporarily hidden
// import { FloatingChat } from '@components/features/chat/FloatingChat';
import {
  AssessmentInfo,
  AssessmentAssignment,
  AssessmentReportSection,
  CancelAssessmentAction,
  CancellationBanner,
  PublishAssessmentAction
} from '@components/features/assessments';
import { AttachmentSection } from '@components/features/attachments';
import EntityActivity from '@components/common/EntityActivity';
import DraftEvidenceSection from '@components/features/evidence/DraftEvidenceSection';
import { ROLE_GROUPS } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import { statusToVariant } from '@utils/badgeMappers';
import { getAssessmentStatusLabel } from '@utils/referenceCatalog';
import { hasOpenConflicts, getTierConflictTooltip } from '@utils/conflictUtils';
import { formatAssessmentId } from '@utils/exportUtils';
import { calculateAssessmentProgress, getManualReportEligibility } from '@utils/assessmentProgress';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import styles from './AssessmentDetailAdminPage.module.css';

const AssessmentDetailAdminPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const [showAssignmentModal, setShowAssignmentModal] = React.useState(false);

  // Ensure chat room exists for this assessment
  useAssessmentChat(id);

  // Fetch assessment details
  const {
    data: assessment,
    isLoading,
    error
  } = useQuery({
    queryKey: ['assessment', id],
    queryFn: () => assessmentService.getAssessment(Number(id)),
    enabled: !!id,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  // Fetch assessment items to calculate progress
  const { data: assessmentItems } = useQuery({
    queryKey: ['assessment-items', id],
    queryFn: () => assessmentService.getAssessmentItems(Number(id)),
    enabled: !!id,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  const handleBack = () => {
    navigate(-1); // Go back to previous page in browser history
  };

  const [isGeneratingReport, setIsGeneratingReport] = React.useState(false);
  const [reportError, setReportError] = React.useState<string | null>(null);

  const handleGenerateReport = async () => {
    if (!id) return;

    setIsGeneratingReport(true);
    setReportError(null);

    try {
      await assessmentService.generateAssessmentReport(Number(id));
      queryClient.invalidateQueries({ queryKey: ['assessment-reports', Number(id)] });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate report';
      setReportError(errorMessage);
      console.error('Error generating report:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleAssign = () => {
    setShowAssignmentModal(true);
  };

  if (isLoading) {
    return (
      <PageLayout>
        <LoadingState message="Loading assessment details..." spinnerSize="large" />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <ErrorState
          title="Assessment Not Found"
          message="The assessment you're looking for could not be found."
          action={
            <Button variant="primary" onClick={handleBack}>
              <ArrowLeft size={16} />
              Go Back
            </Button>
          }
        />
      </PageLayout>
    );
  }

  if (!assessment) {
    return null;
  }

  // Security check
  if (!user || !hasRole(user, ROLE_GROUPS.ADMIN_ONLY)) {
    return (
      <PageLayout>
        <ErrorState
          title="Access Denied"
          message="This page is only accessible to administrators."
          action={
            <Button variant="primary" onClick={handleBack}>
              <ArrowLeft size={16} />
              Go Back
            </Button>
          }
        />
      </PageLayout>
    );
  }

  const canReassign = permissions.canReassignAssessments && assessment.assessment_status !== 'Final';

  const progress = calculateAssessmentProgress(assessmentItems?.items, assessmentItems?.total_items ?? 0);
  const reportEligibility = getManualReportEligibility(assessmentItems?.items);
  // Program Lead sees the assessment's general info only: no process progress or gap breakdown (ADO-1176)
  const showProcessSummary = permissions.isAdminStrict || !permissions.isProgramLead;

  return (
    <PageLayout>
      <DetailPageHeader
        icon={<ClipboardList size={24} />}
        title={`Assessment ${formatAssessmentId(assessment.assessment_id)}`}
        subtitle={assessment.assessment_name}
        backLabel="Go Back"
        onBack={handleBack}
        actions={
          <>
            <PublishAssessmentAction assessment={assessment} notAssessedCount={progress.notAssessed} />
            <CancelAssessmentAction assessment={assessment} />
            <Button
              variant="secondary"
              onClick={handleGenerateReport}
              disabled={isGeneratingReport || !reportEligibility.canGenerate}
              title={reportEligibility.reason}
            >
              <FileText size={16} />
              {isGeneratingReport ? 'Generating...' : 'Generate Report'}
            </Button>
          </>
        }
      />

      <CancellationBanner assessment={assessment} />

      {/* Report Generation Error */}
      {reportError && (
        <div className="alert alert-error" style={{ margin: '16px 0' }}>
          <span>{reportError}</span>
          <button className="alert-close" onClick={() => setReportError(null)} aria-label="Close">
            ×
          </button>
        </div>
      )}

      {/* Simple Status Overview - No collapsible functionality for admins */}
      <div className={styles.statusOverviewContainer} data-test-id="status-overview-container">
        <div className={styles.statusOverview} data-test-id="status-overview">
          <div className={styles.statusCard}>
            <div className={styles.statusIcon}>
              <StatusIcon status="pending" size={24} />
            </div>
            <div className={styles.statusContent}>
              <div className={styles.statusLabel}>Status</div>
              <StatusCell
                hasConflict={hasOpenConflicts(assessment)}
                conflictTooltip={hasOpenConflicts(assessment) ? getTierConflictTooltip(assessment) : undefined}
              >
                <Badge variant={statusToVariant(assessment.assessment_status)}>{getAssessmentStatusLabel(assessment.assessment_status)}</Badge>
              </StatusCell>
            </div>
          </div>

          {showProcessSummary && (
            <>
              <div className={styles.statusCard}>
                <div className={styles.statusIcon}>
                  <StatusIcon status="success" size={24} />
                </div>
                <div className={styles.statusContent}>
                  <div className={styles.statusLabel}>Progress</div>
                  <div className={styles.progressInfo}>
                    <div className={styles.progressBar}>
                      <div className={styles.progressFill} style={{ width: `${progress.percentage}%` }}></div>
                    </div>
                    <span className={styles.progressText}>
                      {progress.percentage}% Complete ({progress.compliant}/{progress.total} items)
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.statusCard}>
                <div className={styles.statusIcon}>
                  <StatusIcon status="error" size={24} />
                </div>
                <div className={styles.statusContent}>
                  <div className={styles.statusLabel}>Total Gaps</div>
                  <div className={styles.gapSummary}>
                    <span className={styles.gapTotal}>{assessment.total_gap_count}</span>
                    <div className={styles.gapBreakdown}>
                      <span className={styles.gapHigh}>H: {assessment.high_gaps}</span>
                      <span className={styles.gapMedium}>M: {assessment.medium_gaps}</span>
                      <span className={styles.gapLow}>L: {assessment.low_gaps}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Admin Management Interface - Always Visible */}
      <div className={styles.adminDetailContent}>
        <div className={styles.adminDetailGrid}>
          {/* Assessment Management Information */}
          <AssessmentInfo
            assessment={assessment}
            canReassign={canReassign}
            onAssign={handleAssign}
            showContractInfo={true}
          />
        </div>

        <DraftEvidenceSection items={assessmentItems?.items ?? []} />

        <AttachmentSection
          entityType="assessment"
          entityId={assessment.assessment_id}
          canManage={true}
        />

        <AssessmentReportSection assessmentId={assessment.assessment_id} />

        {/* Program Lead works with Notes and History here (ADO-1176) */}
        {permissions.isProgramLead && permissions.canAccessNotes && (
          <EntityActivity entityType="assessment" entityId={assessment.assessment_id} title="Assessment Activity" />
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <AssessmentAssignment
          assessment={assessment}
          onClose={() => setShowAssignmentModal(false)}
          onAssigned={() => {
            // Refresh the assessment data
            queryClient.invalidateQueries({ queryKey: ['assessment', id] });
          }}
        />
      )}

      {/* Assessment-specific floating chat */}
      {/* Temporarily hidden */}
      {/* <FloatingChat currentAssessmentId={assessment?.assessment_id} /> */}
    </PageLayout>
  );
};

export default AssessmentDetailAdminPage;
